import { useCallback, useEffect, useState, useRef, useMemo } from "react";

const useNetworkQuality = ({
  probeUrl = "/favicon2.png",
  probeSizeBytes = 50000,
  probeTimeout = 5000,
  safariProbeTimeout = 8000,
} = {}) => {
  const [rawInfo, setRawInfo] = useState({
    status: navigator.onLine ? "unknown" : "offline",
    effectiveType:
      (navigator.connection && navigator.connection.effectiveType) || null,
    downlink: (navigator.connection && navigator.connection.downlink) || null,
    rtt: (navigator.connection && navigator.connection.rtt) || null,
  });

  const lastProbe = useRef(0);
  const mounted = useRef(true);
  const lacksConnectionAPI = useRef(
    typeof navigator.connection === "undefined",
  );
  const isSafari = useRef(
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
  );
  const currentRawInfoRef = useRef(rawInfo);

  useEffect(() => {
    currentRawInfoRef.current = rawInfo;
  }, [rawInfo]);

  const classify = useCallback(
    ({ online, effectiveType, measuredRtt, measuredMbps }) => {
      if (!online) return "offline";
      if (effectiveType) {
        if (effectiveType.includes("2g") || effectiveType.includes("slow-2g"))
          return "poor";
        if (effectiveType === "3g") return "fair";
        if (effectiveType === "4g" || effectiveType === "5g") {
          if (measuredMbps != null && measuredMbps < 2) {
            return "good";
          }
          if (measuredMbps != null) {
            if (measuredMbps < 0.5) return "poor";
            if (measuredMbps < 5) return "fair";
            if (measuredMbps < 20) return "good";
            return "excellent";
          }
          return "good";
        }
      }
      if (measuredRtt != null) {
        if (measuredRtt > 1000) return "poor";
        if (measuredRtt > 300) return "fair";
        if (measuredRtt > 150) return "good";
        return "excellent";
      }
      if (measuredMbps != null) {
        if (measuredMbps < 0.5) return "poor";
        if (measuredMbps < 5) return "fair";
        if (measuredMbps < 20) return "good";
        return "excellent";
      }
      return "unknown";
    },
    [],
  );

  const performProbe = useCallback(
    async (conn) => {
      const now = Date.now();
      if (now - lastProbe.current < 15000) {
        return currentRawInfoRef.current;
      }
      lastProbe.current = now;
      const effectiveTimeout = isSafari.current
        ? safariProbeTimeout
        : probeTimeout;
      const start =
        typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();
      let controller, timer;
      try {
        controller = window.AbortController ? new AbortController() : null;
        timer = setTimeout(() => controller?.abort?.(), effectiveTimeout);
        const resp = await fetch(
          probeUrl + (probeUrl.includes("?") ? "&_=" : "?_=") + now,
          {
            method: "GET",
            cache: "no-store",
            signal: controller?.signal,
          },
        );
        const readStart =
          typeof performance !== "undefined" && performance.now
            ? performance.now()
            : Date.now();
        const blob = await resp.blob();
        const readEnd =
          typeof performance !== "undefined" && performance.now
            ? performance.now()
            : Date.now();
        const durationSec = (readEnd - start) / 1000 || 0.001;
        const bytes = Math.min(blob.size || probeSizeBytes, probeSizeBytes * 2);
        const mbps = (bytes * 8) / (durationSec * 1_000_000);
        const rtt = readStart - start;

        const effectiveType = conn?.effectiveType || null;
        const status = classify({
          online: navigator.onLine,
          effectiveType,
          measuredRtt: rtt,
          measuredMbps: mbps,
        });

        const updatedInfo = {
          status,
          effectiveType,
          downlink: conn?.downlink || null,
          rtt: conn?.rtt || null,
        };

        if (mounted.current) {
          setRawInfo(updatedInfo);
        }
        return updatedInfo;
      } catch (err) {
        if (mounted.current) {
          const status = navigator.onLine ? "poor" : "offline";
          const updatedInfo = {
            status,
            effectiveType: conn?.effectiveType || null,
            downlink: conn?.downlink || null,
            rtt: conn?.rtt || null,
          };
          setRawInfo(updatedInfo);
          return updatedInfo;
        }
        return currentRawInfoRef.current;
      } finally {
        if (timer) clearTimeout(timer);
      }
    },
    [probeUrl, probeTimeout, probeSizeBytes, classify, safariProbeTimeout],
  );

  const checkNetwork = useCallback(async () => {
    if (!mounted.current) return currentRawInfoRef.current;
    const conn = navigator.connection || null;
    return await performProbe(conn);
  }, [performProbe]);

  useEffect(() => {
    mounted.current = true;
    const conn = navigator.connection || null;

    function onOnline() {
      setRawInfo((prev) => ({ ...prev, status: "unknown" }));
      if (lacksConnectionAPI.current || isSafari.current) {
        checkNetwork();
      }
    }
    function onOffline() {
      setRawInfo((prev) => ({ ...prev, status: "offline" }));
    }
    function onConnChange() {
      const currentConn = navigator.connection || null;
      setRawInfo((prev) => ({
        ...prev,
        effectiveType: currentConn?.effectiveType || null,
        downlink: currentConn?.downlink || null,
        rtt: currentConn?.rtt || null,
        status: classify({
          online: navigator.onLine,
          effectiveType: currentConn?.effectiveType || null,
          measuredRtt: null,
          measuredMbps: null,
        }),
      }));
    }

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    if (conn && typeof conn.addEventListener === "function") {
      conn.addEventListener("change", onConnChange);
    }

    return () => {
      mounted.current = false;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      const currentConn = navigator.connection || null;
      if (
        currentConn &&
        typeof currentConn.removeEventListener === "function"
      ) {
        currentConn.removeEventListener("change", onConnChange);
      }
    };
  }, [classify, checkNetwork]);

  const info = useMemo(
    () => rawInfo,
    [rawInfo.status, rawInfo.effectiveType, rawInfo.downlink, rawInfo.rtt],
  );

  return { info, checkNetwork };
};

export default useNetworkQuality;
